import React from 'react';
import {
  ChakraProvider,
  extendTheme,
} from '@chakra-ui/react';
import ChooseLanding from './components/chooselanding';
import AuthBox from './components/authbox';
import BountyList from './components/bounties/BountyList';
import BountyDetail from './components/bounties/BountyDetail';
import CreateBounty from './components/bounties/CreateBounty';
import EditBounty from './components/bounties/EditBounty';
import MyBounties from './components/bounties/MyBounties';
import ReviewBounty from './components/bounties/ReviewBounty';
import SubmitClaim from './components/claims/SubmitClaim';
import ClaimDetail from './components/claims/ClaimDetail';
import MyClaims from './components/claims/MyClaims';
import Settings from './components/profile/Settings';
import UserProfile from './components/profile/UserProfile';
import NotificationsList from './components/notifications/NotificationsList';
import SupportTicketsList from './components/support/SupportTicketsList';
import CreateSupportTicket from './components/support/CreateSupportTicket';
import SupportTicketDetail from './components/support/SupportTicketDetail';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import AdminUserDetail from './components/admin/AdminUserDetail';
import AdminBounties from './components/admin/AdminBounties';
import AdminDisputes from './components/admin/AdminDisputes';
import AdminDisputeDetail from './components/admin/AdminDisputeDetail';
import AdminSupport from './components/admin/AdminSupport';
import AdminSupportDetail from './components/admin/AdminSupportDetail';
import AdminPaymentsIncoming from './components/admin/AdminPaymentsIncoming';
import AdminPaymentsOutgoing from './components/admin/AdminPaymentsOutgoing';
import AdminEmail from './components/admin/AdminEmail';
import AdminVerifications from './components/admin/AdminVerifications';
import AdminVerificationDetail from './components/admin/AdminVerificationDetail';
import Payments from './components/payments/Payments';
import ApplyForVerification from './components/verification/ApplyForVerification';
import ResearcherList from './components/researchers/ResearcherList';
import CreateResearcherProfile from './components/researchers/CreateResearcherProfile';
import ResearcherDetail from './components/researchers/ResearcherDetail';
import MessagesList from './components/messages/MessagesList';
import ConversationDetail from './components/messages/ConversationDetail';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import DisputeResolutionPolicy from './components/DisputeResolutionPolicy';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { AdminRoute } from './components/layout/AdminRoute';
import { Layout } from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import "./firebase";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";

// Force light mode always — override any cached dark mode in localStorage
const forceLightMode = {
  get: () => 'light',
  set: () => {},
  type: 'localStorage',
};

const config = {
  initialColorMode: 'light',
  useSystemColorMode: false,
}

const theme = extendTheme({ config })

const router = createBrowserRouter([
  {
    path: "/",
    element: <ChooseLanding />,
  },
  {
    path: "/login",
    element: <AuthBox />,
  },
  {
    path: "/signup",
    element: <AuthBox initShowSignup={true} />,
  },
  {
    path: "/terms",
    element: <TermsOfService />,
  },
  {
    path: "/privacy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/disputes",
    element: <DisputeResolutionPolicy />,
  },
  // Protected routes
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { path: "home", element: <Navigate to="/tasks" replace /> },
      // Task routes — /tasks/create must come before /tasks/:bountyId
      { path: "tasks", element: <BountyList /> },
      { path: "tasks/create", element: <CreateBounty /> },
      { path: "tasks/:bountyId", element: <BountyDetail /> },
      { path: "tasks/:bountyId/edit", element: <EditBounty /> },
      { path: "my-tasks", element: <MyBounties /> },
      { path: "tasks/:bountyId/review", element: <ReviewBounty /> },
      { path: "tasks/:bountyId/submit", element: <SubmitClaim /> },
      { path: "submissions/:claimId", element: <ClaimDetail /> },
      { path: "my-submissions", element: <MyClaims /> },
      // Legacy redirects
      { path: "bounties", element: <Navigate to="/tasks" replace /> },
      { path: "bounties/create", element: <Navigate to="/tasks/create" replace /> },
      { path: "my-bounties", element: <Navigate to="/my-tasks" replace /> },
      { path: "my-claims", element: <Navigate to="/my-submissions" replace /> },
      { path: "notifications", element: <NotificationsList /> },
      { path: "settings", element: <Settings /> },
      { path: "support", element: <SupportTicketsList /> },
      { path: "support/create", element: <CreateSupportTicket /> },
      { path: "support/:ticketId", element: <SupportTicketDetail /> },
      { path: "users/:userId", element: <UserProfile /> },
      { path: "payments", element: <Payments /> },
      { path: "verify", element: <ApplyForVerification /> },
      // Researcher routes
      { path: "researchers", element: <ResearcherList /> },
      { path: "researchers/create", element: <CreateResearcherProfile /> },
      { path: "researchers/:researcherId", element: <ResearcherDetail /> },
      // Messages routes
      { path: "messages", element: <MessagesList /> },
      { path: "messages/:conversationId", element: <ConversationDetail /> },
    ]
  },
  // Admin routes
  {
    path: "/admin",
    element: (
      <ProtectedRoute>
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: "users", element: <AdminUsers /> },
      { path: "users/:userId", element: <AdminUserDetail /> },
      { path: "verifications", element: <AdminVerifications /> },
      { path: "verifications/:applicationId", element: <AdminVerificationDetail /> },
      { path: "tasks", element: <AdminBounties /> },
      { path: "disputes", element: <AdminDisputes /> },
      { path: "disputes/:arbitrationId", element: <AdminDisputeDetail /> },
      { path: "support", element: <AdminSupport /> },
      { path: "support/:ticketId", element: <AdminSupportDetail /> },
      { path: "payments", element: <Navigate to="/admin/payments/incoming" replace /> },
      { path: "payments/incoming", element: <AdminPaymentsIncoming /> },
      { path: "payments/outgoing", element: <AdminPaymentsOutgoing /> },
      { path: "email", element: <AdminEmail /> },
    ],
  },
]);

function App() {
  return (
    <ChakraProvider theme={theme} colorModeManager={forceLightMode}>
      <CurrencyProvider>
        <AuthProvider>
          <UserProvider>
            <RouterProvider router={router} />
          </UserProvider>
        </AuthProvider>
      </CurrencyProvider>
    </ChakraProvider>
  );
}

export default App;
